import { makeUrl } from "@uplift-ltd/strings";
import Image from "next/image";
import React from "react";
import { ButtonLink } from "components/common/Button";
import Link from "components/common/Link";
import {
  BUNDLE_TYPE_PRODUCT,
  CATEGORY_CERTIFICATE,
  CATEGORY_EVENTS,
  CATEGORY_PPT,
  CATEGORY_SAVINGS_PACKAGES,
  CATEGORY_TOKENS,
  VARIABLE_TYPE_PRODUCT,
} from "constants/site";
import {
  BUY_TOKENS_URL,
  CERTIFICATE_PROGRAMS_URL,
  COURSE_AUTHOR_URL,
  COURSE_URL_BASE,
  EVENT_URL,
  PACKAGES_URL,
  PRIVATE_PRACTICE_TOOLS_URL,
} from "constants/urls";
import { formatUSD, removeHTMLTags } from "helpers/strings";
import logoImg from "images/icon.png";
import styles from "./ResultItem.module.css";

type SearchImage = {
  thumbnail: {
    url: string;
    width: string;
    height: string;
  };
};

type SearchTaxonomy = {
  product_type: string[];
  product_cat: string[];
};

export type SearchData = {
  post_title: string;
  post_type: "page" | "product" | "course_author";
  images: SearchImage;
  content: string;
  slug: string;
  price: number;
  private_practice_tool_snippet: string | null;
  taxonomies: SearchTaxonomy;
};

interface ResultItemProps {
  data: SearchData;
}

const ResultItem = ({ data }: ResultItemProps) => {
  const { post_type, taxonomies, slug } = data;

  const getUrlAndType = () => {
    if (post_type === "course_author") {
      return [makeUrl(COURSE_AUTHOR_URL, { slug }), "Author"];
    }

    if (post_type === "product") {
      if (taxonomies.product_cat?.includes(CATEGORY_PPT)) {
        return [makeUrl(PRIVATE_PRACTICE_TOOLS_URL, { slug }), "Private Practice Form"];
      }

      if (taxonomies.product_cat?.includes(CATEGORY_TOKENS)) {
        return [BUY_TOKENS_URL, "Tokens"];
      }

      if (taxonomies.product_cat?.includes(CATEGORY_CERTIFICATE)) {
        return [makeUrl(CERTIFICATE_PROGRAMS_URL), "Certificate Program"];
      }

      if (taxonomies.product_cat?.includes(CATEGORY_SAVINGS_PACKAGES)) {
        return [makeUrl(PACKAGES_URL, { slug }), "Savings Package"];
      }

      if (taxonomies.product_cat?.includes(CATEGORY_EVENTS)) {
        return [makeUrl(EVENT_URL, { slug }), "Webinar"];
      }

      if (taxonomies.product_type?.includes(BUNDLE_TYPE_PRODUCT)) {
        return [makeUrl(PACKAGES_URL, { slug }), "Package"];
      }

      return [makeUrl(COURSE_URL_BASE, { slug }), "Course"];
    }

    return [slug, "Page"];
  };

  const formatProductPrice = (price: number) => {
    const formattedPrice = formatUSD(price);
    if (post_type === "product" && taxonomies.product_type.includes(VARIABLE_TYPE_PRODUCT)) {
      return `Starting at ${formattedPrice}`;
    }
    return formattedPrice;
  };

  const [url, type] = getUrlAndType();

  return (
    <div className={styles.resultItem}>
      <div className={styles.resultItemImage}>
        <div className={styles.resultItemImageWrapper}>
          <Image
            alt={data?.images?.thumbnail?.url ? data.post_title : "zur-institute"}
            src={data?.images?.thumbnail?.url ? data.images.thumbnail.url : logoImg}
            layout="fill"
            objectFit="cover"
          />
        </div>
      </div>
      <div className={styles.resultItemBody}>
        <div className={styles.resultTitleBox}>
          <p>
            <span className={styles.resultType}>{type}</span>
          </p>
          <Link href={url} className={styles.resultTitle}>
            {data.post_title}
          </Link>
          <p
            className={styles.resultContent}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: removeHTMLTags(data.content || data.private_practice_tool_snippet || ""),
            }}
          />
        </div>
      </div>
      <div className={styles.resultPrice}>
        <div className="text-center font-bold text-2xl text-zur-primary">
          {data.price ? formatProductPrice(data.price) : null}
        </div>
        <div className={styles.resultButton}>
          <ButtonLink theme="light" href={url}>
            Learn More
          </ButtonLink>
        </div>
      </div>
    </div>
  );
};

export default ResultItem;
